/**
 * The Vello typed API client (S097; M12). Composes an injected `HttpTransport` into a small `request` helper
 * that every endpoint group (auth here; geo/quotes/dispatch/execution/payments/notifications/safety in later
 * M12 sessions) builds on. Responsibilities kept here so they stay uniform:
 *  - join base URL + path, set `accept`/`content-type`, mint/propagate an `x-correlation-id`;
 *  - attach the bearer token from the session (via the injected `accessToken` provider) only when a call opts
 *    into auth, and **fail closed** (never hit the network) when an authenticated call has no token;
 *  - attach a device-key proof (ADR-0039) to the calls that opt in, when a proof provider is injected;
 *  - map 2xx → `ok(value)`, non-2xx → `err(ApiError)` (problem+json), transport throw → `err(NETWORK|TIMEOUT)`.
 * It holds no session state of its own — the token provider is the single source of truth (owned by
 * `@vello/app-shell`'s `SessionStore`), so a rotated token is used on the very next call.
 */
import { ok, err, uuidv7 } from '@vello/core';
import { HttpTransportError } from './transport.js';
import { apiErrorFromResponse, transportError } from './errors.js';
const lowerKeys = (h) => Object.fromEntries(Object.entries(h).map(([k, v]) => [k.toLowerCase(), v]));
const isPlainObject = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
export class ApiClient {
    config;
    constructor(config) {
        this.config = config;
    }
    /**
     * D826: the absolute URL of an authenticated resource a native player fetches by itself (a voice message), with the session's `authorization` header - after
     * the session upkeep, so a short-lived token is renewed first. Undefined without a session (never an anonymous URL for a private resource).
     */
    async authorizedSource(path) {
        if (this.config.beforeAuthenticatedRequest !== undefined) {
            try {
                await this.config.beforeAuthenticatedRequest();
            }
            catch {
                /* the server decides whether the token is still good */
            }
        }
        const token = this.config.accessToken?.();
        if (token === undefined || token === '')
            return undefined;
        return { uri: `${this.config.baseUrl}${path}`, headers: { authorization: `Bearer ${token}` } };
    }
    async request(opts) {
        if (opts.auth === true && this.config.beforeAuthenticatedRequest !== undefined) {
            try {
                await this.config.beforeAuthenticatedRequest();
            }
            catch {
                /* session upkeep never blocks the call — the server decides whether the token is still good */
            }
        }
        const first = await this.send(opts);
        if (first.ok || opts.auth !== true || first.error.code !== 'UNAUTHORIZED' || this.config.onUnauthorized === undefined)
            return first;
        let renewed = false;
        try {
            renewed = await this.config.onUnauthorized();
        }
        catch {
            renewed = false;
        }
        return renewed ? this.send(opts) : first;
    }
    async send(opts) {
        const correlationId = (this.config.correlationId ?? uuidv7)();
        const headers = {
            ...(opts.headers !== undefined ? lowerKeys(opts.headers) : {}),
            accept: 'application/json',
            'x-correlation-id': correlationId,
        };
        if (opts.auth === true) {
            const token = this.config.accessToken?.();
            if (token === undefined || token === '') {
                // Fail closed: never send an authenticated call anonymously.
                return err(transportError('MALFORMED_RESPONSE', 'no access token for an authenticated request'));
            }
            headers.authorization = `Bearer ${token}`;
        }
        const url = `${this.config.baseUrl}${opts.path}`;
        let payload = opts.body;
        if (opts.deviceProof !== undefined && this.config.deviceProof !== undefined && isPlainObject(payload)) {
            let proof;
            try {
                proof = await this.config.deviceProof({
                    method: opts.method,
                    url,
                    ...(opts.deviceProof.refreshToken !== undefined ? { refreshToken: opts.deviceProof.refreshToken } : {}),
                });
            }
            catch {
                proof = undefined; // the server's mode decides; a signer fault never blocks the call here
            }
            if (typeof proof === 'string' && proof !== '')
                payload = { ...payload, deviceProof: proof };
        }
        const body = payload === undefined ? undefined : JSON.stringify(payload);
        if (body !== undefined)
            headers['content-type'] = 'application/json';
        const timeoutMs = opts.timeoutMs ?? this.config.defaultTimeoutMs;
        if (opts.expect === 'bytes') {
            // D532: a file download. The body is bytes (an XLSX is not text); an error answer is still problem+json and is mapped like any other.
            const transport = this.config.transport;
            if (transport.sendBytes === undefined)
                return err(transportError('MALFORMED_RESPONSE', 'this transport cannot download files'));
            let file;
            try {
                file = await transport.sendBytes({
                    method: opts.method,
                    url,
                    headers,
                    ...(body !== undefined ? { body } : {}),
                    ...(timeoutMs !== undefined ? { timeoutMs } : {}),
                });
            }
            catch (e) {
                if (e instanceof HttpTransportError && e.kind === 'timeout')
                    return err(transportError('TIMEOUT'));
                return err(transportError('NETWORK', e instanceof Error ? e.message : undefined));
            }
            if (file.status < 200 || file.status >= 300)
                return err(apiErrorFromResponse(file.status, file.headers, new TextDecoder().decode(file.bytes), opts.problemMembers));
            return ok({ bytes: file.bytes, headers: file.headers });
        }
        let response;
        try {
            response = await this.config.transport.send({
                method: opts.method,
                url,
                headers,
                ...(body !== undefined ? { body } : {}),
                ...(timeoutMs !== undefined ? { timeoutMs } : {}),
            });
        }
        catch (e) {
            if (e instanceof HttpTransportError && e.kind === 'timeout')
                return err(transportError('TIMEOUT'));
            return err(transportError('NETWORK', e instanceof Error ? e.message : undefined));
        }
        if (response.status < 200 || response.status >= 300) {
            return err(apiErrorFromResponse(response.status, response.headers, response.body, opts.problemMembers));
        }
        if (opts.expect === 'text')
            return ok(response.body);
        if (opts.expect === 'empty' || response.status === 204 || response.body.length === 0) {
            return ok(undefined);
        }
        try {
            return ok(JSON.parse(response.body));
        }
        catch {
            return err(transportError('MALFORMED_RESPONSE', 'response body was not valid JSON'));
        }
    }
}
//# sourceMappingURL=client.js.map